/**
 * JSON parse / format / compare helpers for the Document Studio editor.
 *
 * The editor keeps the raw text as the single source of truth; these helpers
 * derive the parsed value and detect real (structural) changes so that
 * whitespace or key-reordering alone does not count as an edit.
 */

import type { JsonValue } from "../types";

const INDENT = 2;

export interface ParseResult {
  value: JsonValue | null;
  error: string | null;
}

/**
 * Parse editor text into a JSON value, returning a friendly error instead of
 * throwing. Empty text is treated as an error so Save stays disabled.
 */
export const parseJson = (text: string): ParseResult => {
  if (text.trim() === "") {
    return { value: null, error: "Document is empty" };
  }
  try {
    return { value: JSON.parse(text) as JsonValue, error: null };
  } catch (err) {
    return {
      value: null,
      error: err instanceof Error ? err.message : "Invalid JSON",
    };
  }
};

/** Pretty-print a JSON value with a stable 2-space indent. */
export const stringify = (value: JsonValue): string =>
  JSON.stringify(value, null, INDENT);

/**
 * Re-indent editor text if it parses; otherwise return it unchanged along with
 * the parse error so the caller can surface it.
 */
export const formatJson = (
  text: string,
): { text: string; error: string | null } => {
  const { value, error } = parseJson(text);
  if (error || value === null) {
    return { text, error };
  }
  return { text: stringify(value), error: null };
};

/** Order-insensitive deep equality for JSON values. */
export const deepEqual = (a: JsonValue, b: JsonValue): boolean => {
  if (a === b) {
    return true;
  }
  if (a === null || b === null || typeof a !== typeof b) {
    return false;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    return a.every((item, index) => deepEqual(item, b[index]));
  }
  if (typeof a === "object" && typeof b === "object") {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) {
      return false;
    }
    return aKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(b, key) &&
        deepEqual(
          (a as Record<string, JsonValue>)[key],
          (b as Record<string, JsonValue>)[key],
        ),
    );
  }
  return false;
};
